/**
 * AutoCampaignPanel — the entire "Auto-Pilot" tab of the Campaigns page for
 * one project.
 *
 * Renders one of four states based on the enrollment status:
 *   - never enrolled (`enabled=false`, no runs)   → EnrollmentCard
 *   - paused (`enabled=false`, has prior runs)    → PausedState (Resume button + history)
 *   - enrolled but no linked Telegram chats yet   → TelegramLinkCard
 *   - enrolled + linked                           → ActiveDashboard
 *     (how-it-works strip + status header w/ Pause + cards + chats +
 *      run history + upcoming brief topics + Run Now / Config dialogs)
 */
import { useState } from 'react';
import { toast } from 'sonner';
import { FileText, Pause, Play, SearchCheck, Send, Settings2 } from 'lucide-react';
import type { Project } from '@seo/types';
import { Spinner } from '@/components/spinner';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  Section,
  SectionHeader,
  SectionTitle,
  SectionDescription,
  SectionToolbar,
} from '@/components/ui/section';
import { summarizeApiError } from '@/lib/api';
import { useAutoCampaignStatus } from '@/features/auto-campaign/use-auto-campaign-status';
import { useAutoCampaignHistory } from '@/features/auto-campaign/use-auto-campaign-history';
import { useAutoCampaignBacklog } from '@/features/auto-campaign/use-auto-campaign-backlog';
import {
  useDisableAutoCampaign,
  useEnableAutoCampaign,
} from '@/features/auto-campaign/mutations';
import type { AutoCampaignStatus } from '@/features/auto-campaign/types';
import { EnrollmentCard } from './enrollment-card';
import { TelegramLinkCard } from './telegram-link-card';
import { NextScheduledCard, RecentActivityCard } from './status-cards';
import { LinkedChatsList } from './linked-chats-list';
import { HistoryTable } from './history-table';
import { BacklogTable } from './backlog-table';
import { RunNowDialog } from './run-now-dialog';
import { AddChatDialog } from './add-chat-dialog';
import { ConfigDialog } from './config-dialog';

export interface AutoCampaignPanelProps {
  project: Project;
}

export function AutoCampaignPanel({ project }: AutoCampaignPanelProps): JSX.Element {
  const status = useAutoCampaignStatus(project.id);

  if (status.isLoading) {
    return (
      <Section>
        <div className="flex justify-center p-6">
          <Spinner />
        </div>
      </Section>
    );
  }

  if (status.isError || !status.data) {
    // Silently render nothing on error — a toast will surface any real fetch
    // problem via the api client's global handler.
    return <></>;
  }

  const s = status.data;

  if (!s.enabled) {
    // Distinguish "never set up" from "was running, now paused" — pausing
    // must not dump the user back onto the marketing pitch.
    const previouslyActive = s.fire_count > 0 || s.last_auto_campaign_at !== null;
    if (previouslyActive) return <PausedState project={project} />;
    return (
      <Section>
        <SectionHeader>
          <div>
            <SectionTitle>Auto-Pilot</SectionTitle>
            <SectionDescription>
              Enable to receive automated audit + brief + off-page digests via
              Telegram every Mon/Thu.
            </SectionDescription>
          </div>
        </SectionHeader>
        <EnrollmentCard project={project} />
      </Section>
    );
  }

  if (s.linked_chats_count === 0) {
    return (
      <Section>
        <SectionHeader>
          <div>
            <SectionTitle>Auto-Pilot · Link Telegram</SectionTitle>
            <SectionDescription>
              Auto-pilot is enabled but no Telegram chat is linked. Link a chat
              to receive digests.
            </SectionDescription>
          </div>
        </SectionHeader>
        <TelegramLinkCard project={project} />
      </Section>
    );
  }

  return <ActiveDashboard project={project} status={s} />;
}

// ---------------------------------------------------------------------------
// How it works — compact 3-step explainer strip.
// ---------------------------------------------------------------------------

const HOW_IT_WORKS_STEPS = [
  {
    icon: SearchCheck,
    text: 'We audit your site & find keyword gaps',
    detail: 'Sun & Wed 22:00',
  },
  {
    icon: FileText,
    text: 'AI writes content briefs + social drafts with PDFs',
    detail: null,
  },
  {
    icon: Send,
    text: 'Everything lands in your Telegram',
    detail: 'Mon & Thu 11:00',
  },
] as const;

function HowItWorksStrip({
  schedulerRunning,
}: {
  /** `false` = the platform schedule is paused, so nothing fires on its own. */
  schedulerRunning?: boolean | null;
} = {}): JSX.Element {
  return (
    <Section tone="outline" className="p-4 sm:p-5">
      <div className="text-[11px] font-semibold uppercase tracking-[0.14em] text-muted-foreground">
        How auto-pilot works
      </div>
      <ol className="mt-3 grid gap-3 sm:grid-cols-3">
        {HOW_IT_WORKS_STEPS.map((step, i) => (
          <li key={step.text} className="flex items-start gap-3">
            <span className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-primary/10 text-primary">
              <step.icon className="h-3.5 w-3.5" aria-hidden />
            </span>
            <div className="text-sm">
              <span className="font-medium text-foreground">
                {i + 1}. {step.text}
              </span>
              {step.detail ? (
                <span className="block text-xs text-muted-foreground">
                  {step.detail}
                </span>
              ) : null}
            </div>
          </li>
        ))}
      </ol>
      {schedulerRunning === false ? (
        <p className="mt-3 text-xs font-medium text-amber-600 dark:text-amber-400">
          Automatic runs are currently switched off platform-wide, so these
          times will not fire. Use “Run now” until an admin resumes the
          schedule.
        </p>
      ) : (
        <p className="mt-3 text-xs text-muted-foreground">
          Runs automatically — nothing to do after setup.
        </p>
      )}
    </Section>
  );
}

// ---------------------------------------------------------------------------
// Paused state — enabled=false but the project has prior runs.
// ---------------------------------------------------------------------------

function PausedState({ project }: { project: Project }): JSX.Element {
  const history = useAutoCampaignHistory(project.id, 10);
  const enable = useEnableAutoCampaign(project.id);

  async function handleResume(): Promise<void> {
    try {
      await enable.mutateAsync();
      toast.success('Auto-pilot resumed', {
        description: 'Scheduled runs will fire again from the next slot.',
      });
    } catch (e) {
      const s = summarizeApiError(e, { fallbackTitle: 'Could not resume auto-pilot' });
      toast.error(s.title, { description: s.description });
    }
  }

  return (
    <div className="space-y-4">
      <Section>
        <SectionHeader>
          <div>
            <SectionTitle className="flex items-center gap-2">
              Auto-Pilot · {project.name}
              <Badge variant="secondary">Paused</Badge>
            </SectionTitle>
            <SectionDescription>
              Scheduled runs are stopped. Your history, backlog, and linked
              chats are kept — resume any time.
            </SectionDescription>
          </div>
          <SectionToolbar>
            <Button size="sm" onClick={handleResume} disabled={enable.isPending}>
              {enable.isPending ? (
                <Spinner className="h-3.5 w-3.5" />
              ) : (
                <Play className="h-3.5 w-3.5" />
              )}
              Resume auto-pilot
            </Button>
          </SectionToolbar>
        </SectionHeader>
      </Section>

      <HistoryTable
        items={history.data?.items ?? []}
        isLoading={history.isLoading}
        error={history.error}
      />
    </div>
  );
}

// ---------------------------------------------------------------------------
// Active dashboard.
// ---------------------------------------------------------------------------

function ActiveDashboard({
  project,
  status,
}: {
  project: Project;
  status: AutoCampaignStatus;
}): JSX.Element {
  const history = useAutoCampaignHistory(project.id, 10);
  const backlog = useAutoCampaignBacklog(project.id, 5);
  const historyItems = history.data?.items ?? [];
  const backlogItems = backlog.data?.items ?? [];

  const [runNowOpen, setRunNowOpen] = useState(false);
  const [configOpen, setConfigOpen] = useState(false);
  const [pauseOpen, setPauseOpen] = useState(false);
  const [addChatOpen, setAddChatOpen] = useState(false);

  return (
    <div className="space-y-4">
      <HowItWorksStrip schedulerRunning={status.scheduler_running} />

      <Section>
        <SectionHeader>
          <div>
            <SectionTitle className="flex items-center gap-2">
              Auto-Pilot · {project.name}
              {/* `enabled` only means this project is enrolled. If the
                  platform scheduler is paused nothing fires, and claiming
                  "Active · runs on a schedule" is a lie — that is exactly how
                  12 hand-triggered runs came to look like scheduled ones. */}
              {status.scheduler_running === false ? (
                <Badge variant="warning">Scheduler off</Badge>
              ) : (
                <Badge variant="success">Active</Badge>
              )}
            </SectionTitle>
            <SectionDescription>
              {status.scheduler_running === false
                ? `${project.domain} · enrolled, but automatic runs are switched off platform-wide — use Run now, or ask an admin to resume the schedule.`
                : `${project.domain} · runs on a schedule, digests to Telegram`}
            </SectionDescription>
          </div>
          <SectionToolbar>
            <Button
              variant="ghost"
              size="sm"
              onClick={() => setConfigOpen(true)}
              title="Adjust cadence, brief volume, and off-page platforms"
            >
              <Settings2 className="h-3.5 w-3.5" />
              Settings
            </Button>
            <Button variant="outline" size="sm" onClick={() => setPauseOpen(true)}>
              <Pause className="h-3.5 w-3.5" />
              Pause auto-pilot
            </Button>
          </SectionToolbar>
        </SectionHeader>

        <div className="grid gap-3 md:grid-cols-2">
          <NextScheduledCard
            status={status}
            history={historyItems}
            onRunNow={() => setRunNowOpen(true)}
          />
          <RecentActivityCard status={status} history={historyItems} />
        </div>
      </Section>

      <LinkedChatsList status={status} onAddChat={() => setAddChatOpen(true)} />

      <HistoryTable
        items={historyItems}
        isLoading={history.isLoading}
        error={history.error}
      />

      <BacklogTable
        items={backlogItems}
        projectId={project.id}
        isLoading={backlog.isLoading}
        error={backlog.error}
      />

      <AddChatDialog
        open={addChatOpen}
        onOpenChange={setAddChatOpen}
        projectId={project.id}
      />
      <RunNowDialog
        open={runNowOpen}
        onOpenChange={setRunNowOpen}
        projectId={project.id}
      />
      <ConfigDialog
        open={configOpen}
        onOpenChange={setConfigOpen}
        projectId={project.id}
      />
      <PauseConfirmDialog
        open={pauseOpen}
        onOpenChange={setPauseOpen}
        projectId={project.id}
      />
    </div>
  );
}

// ---------------------------------------------------------------------------
// Pause confirm dialog.
// ---------------------------------------------------------------------------

function PauseConfirmDialog({
  open,
  onOpenChange,
  projectId,
}: {
  open: boolean;
  onOpenChange: (v: boolean) => void;
  projectId: string;
}): JSX.Element {
  const disable = useDisableAutoCampaign(projectId);

  async function handlePause(): Promise<void> {
    try {
      await disable.mutateAsync();
      toast.success('Auto-pilot paused', {
        description: 'Scheduled runs are stopped. Resume any time.',
      });
      onOpenChange(false);
    } catch (e) {
      const s = summarizeApiError(e, { fallbackTitle: 'Could not pause auto-pilot' });
      toast.error(s.title, { description: s.description });
    }
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>Pause auto-pilot?</DialogTitle>
          <DialogDescription>
            Scheduled fires will stop. Your data and history stay — you can
            resume any time.
          </DialogDescription>
        </DialogHeader>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button onClick={handlePause} disabled={disable.isPending}>
            {disable.isPending ? <Spinner className="h-3.5 w-3.5" /> : null}
            Pause auto-pilot
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
